"use client"

import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from "react"
import { useBannerOffset } from "@/lib/contexts/NotificationBannerContext"

interface HeaderHeightContextType {
  headerHeight: number
  headerRef: (node: HTMLDivElement | null) => void
}

const HeaderHeightContext = createContext<HeaderHeightContextType>({
  headerHeight: 80,
  headerRef: () => {},
})

export function HeaderHeightProvider({ children }: { children: ReactNode }) {
  const [headerHeight, setHeaderHeight] = useState(80)
  // The header is pushed down by the notification banner while it shows;
  // --header-h must clear both, or content slides under the header.
  const bannerOffset = useBannerOffset()
  const observerRef = useRef<ResizeObserver | null>(null)

  const headerRef = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect()
      observerRef.current = null
    }

    if (!node) return

    setHeaderHeight(node.getBoundingClientRect().height)

    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setHeaderHeight(entry.borderBoxSize[0].blockSize)
      }
    })
    observer.observe(node)
    observerRef.current = observer
  }, [])

  // Sync to CSS custom property so pages using pt-[--header-h] get it automatically
  useEffect(() => {
    document.documentElement.style.setProperty('--header-h', `${bannerOffset + headerHeight + 12}px`)
  }, [headerHeight, bannerOffset])

  return (
    <HeaderHeightContext.Provider value={{ headerHeight, headerRef }}>
      {children}
    </HeaderHeightContext.Provider>
  )
}

export function useHeaderHeight() {
  return useContext(HeaderHeightContext)
}
